import React from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/PublicLayout";
import { Breadcrumbs, CtaBanner } from "@/components/Marketing";
import WhatsAppButton from "@/components/WhatsAppButton";
import { waMessages } from "@/lib/whatsapp";
import { GUIDES } from "@/data/guides";
import NotFound from "@/pages/NotFound";
import { ArrowRight, CalendarDays } from "lucide-react";

export default function GuideDetail() {
  const { slug } = useParams();
  const g = GUIDES[slug];
  if (!g) return <NotFound />;

  const url = `https://eazycartrack.co.uk/guides/${slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.title,
    description: g.seoDescription,
    datePublished: g.updated,
    dateModified: g.updated,
    mainEntityOfPage: url,
    author: { "@type": "Organization", name: "EazyCarTrack PHV Private Hire Rental" },
    publisher: { "@type": "Organization", name: "EazyCarTrack PHV Private Hire Rental" },
  };

  return (
    <PublicLayout>
      <Seo title={g.title} description={g.seoDescription} path={`/guides/${slug}`} jsonLd={jsonLd} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides", to: "/guides" }, { label: g.title }]} />
      <article className="container-x max-w-3xl py-8">
        <h1 className="font-heading text-3xl font-black uppercase leading-tight tracking-tighter sm:text-4xl">{g.title}</h1>
        <p className="mt-3 inline-flex items-center gap-2 text-sm text-zinc-500">
          <CalendarDays className="h-4 w-4" /> Last updated {new Date(g.updated).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </p>
        <div className="mt-8 space-y-8">
          {g.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-xl font-bold">{s.h}</h2>
              <p className="mt-2 leading-relaxed text-zinc-600">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-lg border border-border bg-surface p-6">
          <h3 className="font-heading text-lg font-bold">Ready to find a car?</h3>
          <p className="mt-1 text-sm text-zinc-600">See what's available now or ask us anything on WhatsApp.</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Link to="/cars" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-ink px-6 font-bold text-white">View Available Cars <ArrowRight className="h-4 w-4" /></Link>
            <WhatsAppButton message={waMessages.general()} context="guide" testid="guide-whatsapp">WhatsApp Us</WhatsAppButton>
          </div>
        </div>

        {g.related?.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Related</h3>
            <ul className="mt-3 flex flex-wrap gap-3">
              {g.related.map((r, i) => (
                <li key={i}><Link to={r.to} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-surface">{r.label}</Link></li>
              ))}
            </ul>
          </div>
        )}
      </article>
      <CtaBanner />
    </PublicLayout>
  );
}
